import React, { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Zap, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Top-level kill switch for ALL SMB outreach automation:
 *   - Background scheduler (queued M1/M2/M3 dispatches)
 *   - Auto-trigger on status change to the configured trigger status
 *   - Auto-promote `preview_qa_passed → preview_sent` on accepted WhatsApp send
 *
 * Manual sends from the WhatsApp chat composer remain available in both states.
 */
export const SmbOutreachAutomationToggle = () => {
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/smb/outreach/automation/enabled');
      if (res.data.success) setDoc(res.data.data);
    } catch { toast.error('Failed to load automation toggle'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (next) => {
    setSaving(true);
    try {
      const res = await api.put('/smb/outreach/automation/enabled', { enabled: next });
      if (res.data.success) {
        setDoc(res.data.data);
        toast.success(res.data.message);
      }
    } catch { toast.error('Failed to update automation toggle'); }
    finally { setSaving(false); }
  };

  const enabled = !!doc?.enabled;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2">
          <Zap className="h-5 w-5 text-amber-500" />SMB Outreach Automation
          {!loading && (
            <Badge variant={enabled ? 'default' : 'secondary'}
                   className="ml-2 text-[10px] uppercase tracking-wide"
                   data-testid="smb-outreach-toggle-status">
              {enabled ? 'Active' : 'Paused'}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          Master switch for the background outreach pipeline — gates the M1/M2/M3
          scheduler, status-change auto-triggers, and the auto-promote
          <span className="px-1 py-0.5 mx-1 rounded bg-muted text-foreground/80 text-[11px]">preview_qa_passed → preview_sent</span>
          on accepted WhatsApp sends. Manual chat composer sends are <strong>not</strong> affected.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-6 p-4 rounded-md border bg-card/50">
          <div className="text-sm">
            <p className="font-medium">
              {enabled ? 'Automation is running.' : 'Automation is paused.'}
            </p>
            {doc?.updated_at && (
              <p className="text-xs text-muted-foreground mt-1">
                Last changed {new Date(doc.updated_at).toLocaleString()}
                {doc.updated_by ? ` · by ${doc.updated_by.slice(0, 8)}…` : ''}
              </p>
            )}
          </div>
          <div className="flex items-center gap-3">
            {(loading || saving) && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
            <Switch
              checked={enabled}
              disabled={loading || saving}
              onCheckedChange={toggle}
              data-testid="smb-outreach-toggle-switch"
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
